import { useEffect, useState, useRef } from 'react';
import { X, Upload, Search, Image, Video, Loader2, Check, Trash2, Mic } from 'lucide-react';
import AudioThumbnail from '../shared/AudioThumbnail';
import toast from 'react-hot-toast';
import { mediaApi } from '../../api/client';
import { useEditorStore } from '../../store/editorStore';

export default function MediaLibraryModal() {
  const toggleMediaLibrary = useEditorStore((s) => s.toggleMediaLibrary);
  const story = useEditorStore((s) => s.story);
  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, search]);

  async function loadAssets() {
    setLoading(true);
    try {
      const result = await mediaApi.list({
        storyId: story?.id,
        type: typeFilter === 'all' ? undefined : typeFilter,
        search: search || undefined,
      });
      const items = result?.items ?? result ?? [];
      setAssets(items.map((a) => ({ ...a, id: a.id ?? a._id })));
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await mediaApi.upload(file, story?.id);
      }
      await loadAssets();
      toast.success(`${files.length} file(s) uploaded!`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function handleInsert(asset) {
    if (!activePageId) return;

    if (asset.type === 'image') {
      addLayer(activePageId, {
        type: 'image', mediaId: asset.id, src: asset.url,
        alt: asset.altText || '', objectFit: 'cover',
      }, { x: 0, y: 0, width: 100, height: 50 });
    } else if (asset.type === 'video') {
      addLayer(activePageId, {
        type: 'video', mediaId: asset.id, src: asset.url,
        poster: asset.posterUrl, autoplay: true, loop: true, muted: true,
      }, { x: 0, y: 0, width: 100, height: 100 });
    } else if (asset.type === 'audio') {
      addLayer(activePageId, {
        type: 'audio', mediaId: asset.id, src: asset.url,
        autoplay: true, loop: true, showIcon: true,
      }, { x: 5, y: 5, width: 20, height: 10 });
    }

    toast.success(`Added ${asset.type} to page`);
    toggleMediaLibrary();
  }

  async function handleDelete(asset) {
    if (!window.confirm(`Delete "${asset.originalFilename}"?`)) return;
    try {
      await mediaApi.delete(asset.id);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      if (selected?.id === asset.id) setSelected(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden"
           style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--t-border)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--t-text)' }}>Media Library</h2>
          <button onClick={toggleMediaLibrary} className="icon-btn">
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 p-3" style={{ borderBottom: '1px solid var(--t-border)' }}>
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search media..."
              className="input-field pl-9 text-sm"
            />
          </div>

          {/* Type filter */}
          <div className="flex gap-1">
            {(['all', 'image', 'video', 'audio']).map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1.5 text-xs rounded-lg capitalize transition-colors
                  ${typeFilter === t ? 'bg-indigo-600 text-white' : ''}`}
                style={typeFilter !== t ? { background: 'var(--t-panel)', color: 'var(--t-muted)' } : {}}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Upload */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary flex items-center gap-2 flex-shrink-0"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 size={20} className="animate-spin text-indigo-500" />
              </div>
            ) : assets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <Image size={32} className="mb-2" style={{ color: 'var(--t-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--t-muted)' }}>No media found</p>
                <p className="text-xs mt-1" style={{ color: 'var(--t-muted)' }}>Upload images, videos, or audio files</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {assets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => setSelected(asset === selected ? null : asset)}
                    className={`relative aspect-square rounded-lg overflow-hidden group
                      hover:ring-2 hover:ring-indigo-500 transition-all
                      ${selected?.id === asset.id ? 'ring-2 ring-indigo-500' : ''}`}
                    style={{ background: 'var(--t-panel)' }}
                    title={asset.originalFilename}
                  >
                    {asset.type === 'image' ? (
                      <img
                        src={asset.thumbnailUrl || asset.url}
                        alt={asset.altText || ''}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : asset.type === 'video' ? (
                      <div className="w-full h-full relative" style={{ background: '#18181b' }}>
                        <video src={asset.url} className="w-full h-full object-cover" preload="metadata" muted playsInline />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div style={{ background: 'rgba(0,0,0,0.45)', borderRadius: '50%', padding: 4, display: 'flex' }}>
                            <Video size={14} className="text-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full">
                        <AudioThumbnail filename={asset.originalFilename} size="md" />
                      </div>
                    )}

                    {/* Selected indicator */}
                    {selected?.id === asset.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-indigo-600 rounded-full
                                      flex items-center justify-center">
                        <Check size={11} className="text-white" />
                      </div>
                    )}

                    {/* Delete on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(asset); }}
                      className="absolute top-1 left-1 w-5 h-5 bg-red-900/80 rounded-full
                                 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={10} className="text-red-400" />
                    </button>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview panel */}
          {selected && (
            <div className="w-64 p-4 overflow-y-auto flex flex-col" style={{ borderLeft: '1px solid var(--t-border)' }}>
              <div className="aspect-video rounded-lg overflow-hidden mb-3 flex items-center justify-center"
                   style={{ background: 'var(--t-panel)' }}>
                {selected.type === 'image' ? (
                  <img src={selected.url} alt={selected.altText || ''} className="w-full h-full object-contain" />
                ) : selected.type === 'video' ? (
                  <video src={selected.url} poster={selected.posterUrl} controls className="w-full h-full" />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Mic size={24} style={{ color: 'var(--t-muted)' }} />
                    <audio src={selected.url} controls className="w-full" />
                  </div>
                )}
              </div>

              <p className="text-sm font-medium truncate mb-1" style={{ color: 'var(--t-text2)' }}>{selected.originalFilename}</p>
              <p className="text-xs mb-1" style={{ color: 'var(--t-muted)' }}>{(selected.size / 1024).toFixed(1)} KB</p>
              {selected.width && selected.height && (
                <p className="text-xs mb-3" style={{ color: 'var(--t-muted)' }}>{selected.width}×{selected.height}px</p>
              )}

              <button
                onClick={() => handleInsert(selected)}
                className="btn-primary w-full mt-auto"
              >
                Insert to Page
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
